import React, { useState, ChangeEvent, FormEvent, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Modal } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from '../../../api/axios';
import AdminHeader from '../layout/AdminHeader';
import BusinessCategorySelector from './BusinessCategorySelector';
import { PASSWORD_VALIDATION } from '../../../utils/Constants';
import './AdminClientForm.css';
import { validateEmail, containsEmoji, validatePassword, validatePhoneNumber } from '../../../utils/Utils';
import { ApiError } from '../../../types/errors';
import AdminNavigation from '../layout/AdminNavigation';
import { BusinessCategory1, BusinessCategory2, BusinessCategory3 } from '../../../types';

interface FormData {
  name: string;
  email: string;
  contact_person: string;
  phone: string;
  business_category1_id: number;
  business_category2_id: number;
  business_category3_id: number;
  password: string;
  internal_memo: string;
  [key: string]: string | number;
}

interface FormErrors {
  name?: string;
  email?: string;
  contact_person?: string;
  phone?: string;
  business_category1_id?: string;
  business_category2_id?: string;
  business_category3_id?: string;
  password?: string;
  internal_memo?: string;
  [key: string]: string | undefined;
}

const AdminClientForm: React.FC = () => {
  const navigate = useNavigate();

  const generatePassword = (): string => {
    const letters = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = PASSWORD_VALIDATION.SYMBOLS;
    const allCharacters = letters + numbers + symbols;

    let password = '';
    password += letters.charAt(Math.floor(Math.random() * letters.length));
    password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    password += symbols.charAt(Math.floor(Math.random() * symbols.length));

    for (let i = 3; i < PASSWORD_VALIDATION.MIN_LENGTH; i++) {
      password += allCharacters.charAt(Math.floor(Math.random() * allCharacters.length));
    }

    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    contact_person: '',
    phone: '',
    business_category1_id: 0,
    business_category2_id: 0,
    business_category3_id: 0,
    password: generatePassword(),
    internal_memo: ''
  });

  const [allCategory1Data, setAllCategory1Data] = useState<BusinessCategory1[]>([]);
  const [allCategory2Data, setAllCategory2Data] = useState<BusinessCategory2[]>([]);
  const [allCategory3Data, setAllCategory3Data] = useState<BusinessCategory3[]>([]);
  const [categoryNames, setCategoryNames] = useState<{
    category1?: string;
    category2?: string;
    category3?: string;
  }>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  useEffect(() => {
    loadAllCategoryData();
  }, []);

  const loadAllCategoryData = async () => {
    try {
      const token = localStorage.getItem('admin_token');

      const [response1, response2, response3] = await Promise.all([
        axios.get('/admin/business-categories/1', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/admin/business-categories/2', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get('/admin/business-categories/3', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setAllCategory1Data(response1.data);
      setAllCategory2Data(response2.data);
      setAllCategory3Data(response3.data);
    } catch (error) {
      console.error('業界カテゴリデータの取得に失敗しました:', error);
    } finally {
    }
  };

  const loadCategoryNames = async (category1Id?: number, category2Id?: number, category3Id?: number) => {
    try {
      const names: { category1?: string; category2?: string; category3?: string } = {};

      if (category1Id) {
        const category1 = allCategory1Data.find((cat: BusinessCategory1) => cat.id === category1Id);
        if (category1) names.category1 = category1.name;

        if (category2Id) {
          const category2 = allCategory2Data.find((cat: BusinessCategory2) => cat.business_category1_id === category1Id && cat.id === category2Id);
          if (category2) names.category2 = category2.name;

          if (category3Id) {
            const category3 = allCategory3Data.find((cat: BusinessCategory3) => 
              cat.business_category1_id === category1Id && 
              cat.business_category2_id === category2Id && 
              cat.id === category3Id
            );
            if (category3) names.category3 = category3.name;
          }
        }
      }

      setCategoryNames(names);
    } catch (error) {
      console.error('業界カテゴリ名の取得に失敗しました:', error);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateForm = async (): Promise<FormErrors> => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'クライアント名を入力してください。';
    } else if (containsEmoji(formData.name)) {
      newErrors.name = '絵文字は使用できません。';
    }

    if (!formData.contact_person.trim()) {
      newErrors.contact_person = '担当者名を入力してください。';
    } else if (containsEmoji(formData.contact_person)) {
      newErrors.contact_person = '絵文字は使用できません。';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = '電話番号を入力してください。';
    } else if (!validatePhoneNumber(formData.phone)) {
      newErrors.phone = '不正な電話番号です。';
    } else if (containsEmoji(formData.phone)) {
      newErrors.phone = '絵文字は使用できません。';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'メールアドレスを入力してください。';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = '不正なメールアドレスです。';
    } else if (containsEmoji(formData.email)) {
      newErrors.email = '絵文字は使用できません。';
    } else {
      try {
        const response = await axios.get(`/admin/users/check-email?email=${encodeURIComponent(formData.email)}`);
        if (response.data.exists) {
          newErrors.email = '既に登録済みのメールアドレスです。';
        }
      } catch (error) {
        console.error('Email check error:', error);
        newErrors.email = 'メールアドレスの重複チェック中にエラーが発生しました。もう一度お試しください。';
      }
    }

    if (!formData.password) {
      newErrors.password = 'パスワードを入力してください。';
    } else if (!validatePassword(formData.password)) {
      newErrors.password = PASSWORD_VALIDATION.ERROR_MESSAGE;
    } else if (containsEmoji(formData.password)) {
      newErrors.password = '絵文字は使用できません。';
    }


    if (formData.internal_memo && containsEmoji(formData.internal_memo)) {
      newErrors.internal_memo = '絵文字は使用できません。';
    }

    return newErrors;
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>): Promise<void> => {
    if (e.type === 'submit') {
      e.preventDefault();
    }

    const newErrors = await validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    loadCategoryNames(formData.business_category1_id, formData.business_category2_id, formData.business_category3_id);

    setShowConfirmModal(true);
    setErrors({});
  };

  const handleConfirmSubmit = async (): Promise<void> => {
    setIsSubmitting(true);

    try {
      const token = localStorage.getItem('admin_token');

      const userData = {
        name: formData.name,
        email: formData.email,
        contact_person: formData.contact_person,
        phone: formData.phone,
        business_category1_id: formData.business_category1_id || null,
        business_category2_id: formData.business_category2_id || null,
        business_category3_id: formData.business_category3_id || null,
        password: formData.password,
        internal_memo: formData.internal_memo
      };

      const response = await axios.post('/admin/users', userData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setShowConfirmModal(false);
      alert('クライアントの登録に成功しました。');
      navigate('/admin/clients');
    } catch (error: unknown) {
      const axiosError = error as ApiError;
      console.error('Error creating user:', axiosError);

      if (axiosError.response?.data?.errors) {
        const responseErrors = axiosError.response.data.errors;
        const formattedErrors: FormErrors = {};

        if (responseErrors.name) formattedErrors.name = responseErrors.name;
        if (responseErrors.email) formattedErrors.email = responseErrors.email;
        if (responseErrors.contact_person) formattedErrors.contact_person = responseErrors.contact_person;
        if (responseErrors.phone) formattedErrors.phone = responseErrors.phone;
        if (responseErrors.business_category1_id) formattedErrors.business_category1_id = responseErrors.business_category1_id;
        if (responseErrors.business_category2_id) formattedErrors.business_category2_id = responseErrors.business_category2_id;
        if (responseErrors.business_category3_id) formattedErrors.business_category3_id = responseErrors.business_category3_id;

        setErrors(formattedErrors);
      } else {
        alert('クライアントの登録に失敗しました。管理者にお問い合わせください。');
      }

      setShowConfirmModal(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="candidate-form-container">
      <AdminHeader />

      <Container>
        <AdminNavigation />
        <Row>
          <Col md={8}>
            <div className="form-container">
              <div className="form-header">
                新規クライアント登録
              </div>
              <div className="form-body">
                <div className="respondent-section">
                  <Form onSubmit={handleSubmit}>
                    <Row className="form-row">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="form-label">クライアント名 <span className="required-mark">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            isInvalid={!!errors.name}
                            placeholder="（例）株式会社サンプル"
                            style={{ maxWidth: 'unset' }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="form-row">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="form-label">担当者名 <span className="required-mark">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="contact_person"
                            value={formData.contact_person}
                            onChange={handleChange}
                            isInvalid={!!errors.contact_person}
                            placeholder="（例）山田太郎"
                            style={{ maxWidth: 'unset' }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.contact_person}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="form-label">電話番号 <span className="required-mark">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            isInvalid={!!errors.phone}
                            placeholder="（例）03-1234-5678"
                            style={{ maxWidth: 'unset' }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.phone}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="form-row">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="form-label">メールアドレス <span className="required-mark">*</span></Form.Label>
                          <Form.Control
                            type="text"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            isInvalid={!!errors.email}
                            placeholder="（例）contact@example.com"
                            style={{ maxWidth: 'unset' }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="form-row">
                      <Col md={6} className="category-select-area mb-2">
                        <Form.Group>
                          <Form.Label className="form-label">業界・業種</Form.Label>
                          <BusinessCategorySelector
                            allCategory1Data={allCategory1Data}
                            allCategory2Data={allCategory2Data}
                            allCategory3Data={allCategory3Data}
                            category1Value={formData.business_category1_id}
                            category2Value={formData.business_category2_id}
                            category3Value={formData.business_category3_id}
                            onCategory1Change={(value) => setFormData(prev => ({ 
                              ...prev, 
                              business_category1_id: value,
                              business_category2_id: 0,
                              business_category3_id: 0
                            }))}
                            onCategory2Change={(value) => setFormData(prev => ({ 
                              ...prev, 
                              business_category2_id: value,
                              business_category3_id: 0
                            }))}
                            onCategory3Change={(value) => setFormData(prev => ({ ...prev, business_category3_id: value }))}
                            errors={errors}
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="form-row">
                      <Col md={6}>
                        <Form.Group>
                          <Form.Label className="form-label">パスワード <span className="required-mark">*</span></Form.Label>
                          <Form.Control
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            isInvalid={!!errors.password}
                            placeholder="パスワードを入力してください"
                            style={{ maxWidth: 'unset' }}
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.password}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="form-row">
                      <Col md={12}>
                        <Form.Group>
                          <Form.Label className="form-label">社内向けメモ</Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="internal_memo"
                            value={formData.internal_memo}
                            onChange={handleChange}
                            isInvalid={!!errors.internal_memo}
                            placeholder="社内向けメモを入力してください"
                            className="form-textarea"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.internal_memo}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                </div>
              </div>
            </div>
          </Col>
          <Col md={4}>
            <div className="confirmation-button-container">
              <Button 
                variant="success"
                className="submit-button" 
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                確認画面へ
              </Button>
              <p className="note-text text-left">必須項目を入力してください</p>
            </div>
          </Col>
        </Row>

        <Modal
          show={showConfirmModal} 
          onHide={() => setShowConfirmModal(false)}
          centered
          className="user-confirm-modal"
          backdrop="static"  // Prevent close when clicking outside
          keyboard={false}   // Optional: Prevent closing with ESC key
        >
          <Modal.Header closeButton>
            <Modal.Title>新規クライアント登録確認</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">クライアント名</div>
                <div className="candidate-detail-value">{formData.name}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">担当者名</div>
                <div className="candidate-detail-value">{formData.contact_person || '-'}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">電話番号</div>
                <div className="candidate-detail-value">{formData.phone || '-'}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">メールアドレス</div>
                <div className="candidate-detail-value">{formData.email}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">業界・業種</div>
                <div className="candidate-detail-value">
                  {formData.business_category1_id ? (
                    <div>
                      {categoryNames.category1 && <div>{categoryNames.category1}</div>}
                      {categoryNames.category2 && <div>{categoryNames.category2}</div>}
                      {categoryNames.category3 && <div>{categoryNames.category3}</div>}
                    </div>
                  ) : '未設定'}
                </div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">パスワード</div>
                <div className="candidate-detail-value">{'*'.repeat(formData.password.length)}</div>
              </div>
              <div className="candidate-detail-item">
                <div className="candidate-detail-label">社内向けメモ</div>
                <div className="candidate-detail-value internal-memo">{formData.internal_memo || '-'}</div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="success" 
              className="save-button" 
              onClick={handleConfirmSubmit}
              disabled={isSubmitting}
            >
              登録
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
};

export default AdminClientForm;
